import { useState, useCallback, useMemo, useEffect } from 'react';
import { DEFAULT_LIST_PAGE_SIZE } from '../pagination/paginationConstants';

/**
 * Manages pagination state for server-paged list endpoints.
 */
export function usePagination(initialPageSize = DEFAULT_LIST_PAGE_SIZE) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => {
    setPageSize(initialPageSize);
    setPage(0);
  }, [initialPageSize]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  const applyPagedMeta = useCallback((meta) => {
    setPage(meta.page ?? 0);
    setTotalElements(meta.totalElements ?? 0);
    setTotalPages(meta.totalPages ?? 0);
    setHasNext(Boolean(meta.hasNext));
    setHasPrevious(Boolean(meta.hasPrevious));
  }, []);

  const resetPage = useCallback(() => {
    setPage(0);
  }, []);

  const goToPage = useCallback((nextPage) => {
    setPage(Math.max(0, nextPage));
  }, []);

  const goToNextPage = useCallback(() => {
    if (hasNext) {
      setPage((current) => current + 1);
    }
  }, [hasNext]);

  const goToPreviousPage = useCallback(() => {
    if (hasPrevious) {
      setPage((current) => Math.max(0, current - 1));
    }
  }, [hasPrevious]);

  const pagination = useMemo(
    () => ({
      page,
      pageSize,
      totalElements,
      totalPages,
      hasNext,
      hasPrevious,
    }),
    [page, pageSize, totalElements, totalPages, hasNext, hasPrevious]
  );

  return {
    page,
    pageSize,
    pagination,
    applyPagedMeta,
    resetPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
  };
}
